import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ProjectPhoto {
  id: string;
  image_url: string;
  roof_type: "metal" | "tile" | "shingle" | "flat";
  location: string | null;
  description: string | null;
  is_before_after: boolean;
  before_image_url: string | null;
  display_order: number;
  is_featured: boolean;
  transformation_type: string | null;
  before_description: string | null;
  state?: string | null;
  manufacturer?: string | null;
  product_line?: string | null;
  color?: string | null;
  architectural_style?: string | null;
  year_completed?: number | null;
  square_footage?: number | null;
  warranty_years?: number | null;
  project_duration_days?: number | null;
  scope_of_work?: string | null;
  materials_used?: string[] | null;
  alt_text?: string | null;
  slug?: string | null;
}

export const useProjectPhotos = (filter?: "all" | "metal" | "tile" | "shingle" | "flat", state?: string) => {
  return useQuery({
    queryKey: ["project-photos", filter, state],
    queryFn: async () => {
      let query = supabase
        .from("project_photos")
        .select("*")
        .order("display_order", { ascending: true });
      
      if (filter && filter !== "all") {
        query = query.eq("roof_type", filter);
      }

      if (state) {
        query = query.eq("state", state);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data as ProjectPhoto[];
    },
  });
};

export const useFeaturedPhotos = () => {
  return useQuery({
    queryKey: ["featured-photos"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_photos")
        .select("*")
        .eq("is_featured", true)
        .order("display_order", { ascending: true })
        .limit(6);
      if (error) throw error;
      return data as ProjectPhoto[];
    },
  });
};

export const useProjectPhotosByLocation = (location: string) => {
  return useQuery({
    queryKey: ["project-photos-location", location],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_photos")
        .select("*")
        .ilike("location", `%${location}%`)
        .order("display_order", { ascending: true })
        .limit(8);
      if (error) throw error;
      return data as ProjectPhoto[];
    },
    enabled: !!location,
  });
};
